import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import { AuthUser, authService } from "./authService";
import { logger } from "./utils/logger";
import "./components/Login.css";

interface AuthContextValue {
    user: AuthUser | null;
    isLoading: boolean;
    refreshUser: () => Promise<AuthUser | null>;
    logout: () => Promise<void>;
}

interface AuthGuardProps {
    children: ReactNode;
}

interface RedirectLocationState {
    from?: { pathname?: string };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: AuthGuardProps): ReactNode {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Состояние авторизации определяется только ответом /api/auth/me:
    // куку hawk.sid из JS не видно и проверять её нечем.
    const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
        try {
            const me = await authService.me();
            setUser(me);

            return me;
        } catch (error) {
            logger.debug("auth/me failed", (error as AxiosError)?.response?.status);
            setUser(null);

            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const logout = useCallback(async (): Promise<void> => {
        try {
            await authService.logout();
        } finally {
            setUser(null);
        }
    }, []);

    return (
        <AuthContext.Provider value={{ user, isLoading, refreshUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used inside AuthProvider");
    }

    return context;
}

export function RequireAuth({ children }: AuthGuardProps): ReactNode {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return <AuthLoading />;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
}

// Обратная сторона RequireAuth: с живой сессией форма входа не нужна,
// пользователь должен сразу попадать на консоль.
export function RedirectIfAuthenticated({ children }: AuthGuardProps): ReactNode {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return <AuthLoading />;
    }

    // Сюда мог привести 401 с эндпоинта, который сессию ещё не понимает.
    // /api/auth/me при этом отвечает 200, и редирект на "/" ушёл бы в цикл.
    const isSessionExpired =
        new URLSearchParams(location.search).get("session") === "expired";

    if (user && !isSessionExpired) {
        const from = (location.state as RedirectLocationState | null)?.from?.pathname;
        return <Navigate to={from ?? "/"} replace />;
    }

    return children;
}

function AuthLoading(): ReactNode {
    return (
        <div className="form-page">
            <div className="form-page-container">
                <div className="console-logo">Air-link Console Loading...</div>
            </div>
        </div>
    );
}
